import React, { useEffect, useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import styled from 'styled-components';
import { clearAppSession } from '../auth-utils';
import { mapMsalAccountToAppUser } from '../auth/mapMsalAccount';
import { establishAppSession } from '../auth/establishSession';
import { getMsalRedirectResult } from '../auth/msalBootstrap';
import { loginRequest } from '../config/azureAd';
import { createMicrosoftSession, navigateAfterLogin, verifyServerSession } from '../services/authService';

const CallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const LoadingText = styled.p`
  margin-top: 10px;
  color: #666;
  font-size: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6b21a8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 40px;
  max-width: 500px;
  margin: 0 auto;
  background: white;
  border-radius: 0.7rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ErrorTitle = styled.h3`
  color: #d73527;
  margin-bottom: 15px;
`;

const ErrorMessage = styled.p`
  color: #666;
  margin-bottom: 20px;
  word-break: break-word;
`;

const ReturnButton = styled.button`
  background: #6b21a8;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background: #553285;
  }
`;

function resolveAccount(
  responseAccount: AccountInfo | null | undefined,
  cachedAccounts: AccountInfo[]
): AccountInfo | null {
  if (responseAccount) {
    return responseAccount;
  }
  return cachedAccounts[0] ?? null;
}

async function resolveIdToken(
  instance: ReturnType<typeof useMsal>['instance'],
  response: AuthenticationResult | null,
  account: AccountInfo
): Promise<string | null> {
  if (response?.idToken) {
    return response.idToken;
  }

  const silent = await instance.acquireTokenSilent({
    ...loginRequest,
    account,
  });
  return silent.idToken || null;
}

const SSOCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const callbackProcessedRef = useRef(false);
  const { instance } = useMsal();

  useEffect(() => {
    if (callbackProcessedRef.current) {
      return;
    }
    callbackProcessedRef.current = true;

    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const azureError = urlParams.get('error');
      const azureErrorDescription = urlParams.get('error_description');

      if (azureError) {
        setStatus('error');
        setError(azureErrorDescription || azureError);
        return;
      }

      // Never trust localStorage alone — cookie must be minted via /aiapps-auth/session.
      clearAppSession();

      try {
        // Bootstrap already consumed the redirect — prefer that stored result.
        const response =
          getMsalRedirectResult() ||
          (await instance.handleRedirectPromise({
            navigateToLoginRequestUrl: false,
          }));

        const account = resolveAccount(response?.account, instance.getAllAccounts());

        if (!account) {
          setStatus('error');
          setError('Sign-in was not completed. No Microsoft account was found after redirect.');
          return;
        }

        instance.setActiveAccount(account);

        const idToken = await resolveIdToken(instance, response, account);
        if (!idToken) {
          setStatus('error');
          setError('Microsoft sign-in completed, but the identity token was not returned.');
          return;
        }

        const session = await createMicrosoftSession(idToken);
        const appUser = mapMsalAccountToAppUser(account);
        establishAppSession(
          {
            ...appUser,
            ...session.user,
          },
          session.expiry,
          session.authProvider
        );

        const serverOk = await verifyServerSession();
        if (!serverOk) {
          clearAppSession();
          setStatus('error');
          setError(
            'Microsoft sign-in succeeded, but the app session cookie was not accepted (/aiapps-auth/me returned 401). Please try again.'
          );
          return;
        }

        // Clean OAuth params from the URL before navigating away.
        window.history.replaceState({}, document.title, '/sso-callback');

        setStatus('success');
        setTimeout(() => navigateAfterLogin(), 200);
      } catch (err) {
        console.error('SSO Callback error:', err);
        clearAppSession();
        setStatus('error');
        setError(err instanceof Error ? err.message : 'An error occurred during sign-in.');
      }
    };

    handleCallback();
  }, [instance]);

  if (status === 'success') {
    return (
      <CallbackContainer>
        <LoadingContainer>
          <LoadingText style={{ color: '#22c55e', fontWeight: 'bold' }}>
            Authentication successful! Redirecting...
          </LoadingText>
        </LoadingContainer>
      </CallbackContainer>
    );
  }

  if (status === 'loading') {
    return (
      <CallbackContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Completing sign-in...</LoadingText>
        </LoadingContainer>
      </CallbackContainer>
    );
  }

  return (
    <CallbackContainer>
      <ErrorContainer>
        <ErrorTitle>Authentication Error</ErrorTitle>
        <ErrorMessage>{error}</ErrorMessage>
        <ReturnButton
          onClick={() => {
            window.location.href = '/login';
          }}
        >
          Return to Login
        </ReturnButton>
      </ErrorContainer>
    </CallbackContainer>
  );
};

export default SSOCallback;
